<template>
  <section class="p-6 flex flex-col gap-6">
    <!-- Breadcrumb -->
    <nav class="text-gray-600 text-sm">
      <span class="font-semibold cursor-pointer" @click="$router.push('/')">
        Home
      </span>
      <span class="mx-2">/</span>
      <span class="text-gray-800 font-bold">Partners</span>
    </nav>

    <h3 class="font-bold text-2xl">Partners</h3>

    <!-- Search & Add -->
    <div class="flex items-center justify-between w-full mb-4">
      <input
        v-model="search"
        @keyup.enter="fetchPartners"
        placeholder="Search by name or website..."
        class="border border-gray-300 rounded-md px-4 py-2 w-1/3"
      />

      <button
        @click="$router.push({ name: 'AddPartners' })"
        class="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
      >
        + Add Partner
      </button>
    </div>

    <!-- Table -->
    <div class="overflow-x-auto">
      <table class="min-w-full border-collapse bg-white rounded-lg shadow">
        <thead>
          <tr class="bg-gray-100 text-left text-sm font-semibold">
            <th class="px-4 py-2">Name</th>
            <th class="px-4 py-2">Website</th>
            <th class="px-4 py-2">Sort Order</th>
            <th class="px-4 py-2">Logo</th>
            <th class="px-4 py-2">Created At</th>
            <th class="px-4 py-2">Updated At</th>
            <th class="px-4 py-2 text-center">Actions</th>
          </tr>
        </thead>

        <tbody>
          <tr
            v-for="partner in filteredPartners"
            :key="partner.id"
            class="border-t hover:bg-gray-50 text-sm"
          >
            <!-- Name -->
            <td class="px-4 py-2">
              <div class="font-semibold text-gray-900">
                {{ partner.name }}
              </div>
              <div class="text-xs text-gray-500">
                ID: {{ partner.id }}
              </div>
            </td>

            <!-- Website -->
            <td class="px-4 py-2 max-w-xs">
              <span v-if="partner.website_url" class="block truncate">
                <a
                  :href="partner.website_url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-blue-600 hover:underline"
                >
                  {{ partner.website_url }}
                </a>
              </span>
              <span v-else class="text-gray-500">-</span>
            </td>

            <!-- Sort Order -->
            <td class="px-4 py-2">
              {{ partner.sort_order ?? "-" }}
            </td>

            <!-- Logo (✅ تم الحل هنا) -->
            <td class="px-4 py-2">
              <div v-if="getPartnerLogo(partner)">
                <img
                  :src="getPartnerLogo(partner)"
                  :alt="partner.name"
                  class="w-12 h-12 object-contain rounded shadow"
                />
              </div>

              <span v-else class="text-gray-400 text-xs italic">
                No logo
              </span>
            </td>

            <!-- Created At -->
            <td class="px-4 py-2 whitespace-nowrap">
              {{ formatDate(partner.created_at) }}
            </td>

            <!-- Updated At -->
            <td class="px-4 py-2 whitespace-nowrap">
              {{ formatDate(partner.updated_at) }}
            </td>

            <!-- Actions -->
            <td class="px-4 py-2 text-center whitespace-nowrap">
              <button
                @click="$router.push({ name: 'EditPartners', params: { id: partner.id } })"
                class="text-green-600 hover:underline mx-1"
              >
                Edit
              </button>

              <button
                @click="deletePartner(partner.id)"
                class="text-red-600 hover:underline mx-1"
              >
                Delete
              </button>
            </td>
          </tr>

          <tr v-if="filteredPartners.length === 0">
            <td colspan="7" class="text-center py-4 text-gray-500">
              No partners found.
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import apiClient from "@/apiClient";

/* =========================
   STATE
========================= */
const partners = ref([]);
const search = ref("");

/* =========================
   FILE BASE URL
   ========================= */
const FILE_BASE_URL = "http://127.0.0.1:8000";

/* =========================
   HELPERS
========================= */

function getPartnerLogo(partner) {
  if (!partner?.logo_path) return "";
  return `${FILE_BASE_URL}/files/partners/${partner.logo_path}`;
}


function formatDate(date) {
  if (!date) return "-";
  return new Date(date).toLocaleString();
}

/* =========================
   API
========================= */
async function fetchPartners() {
  try {
    const res = await apiClient.get("/admin/partners")


    partners.value = res.data?.data?.partners || [];
  } catch (error) {
    console.error("Error fetching partners:", error);
    partners.value = [];
  }
}

/* =========================
   COMPUTED
========================= */
const filteredPartners = computed(() => {
  if (!search.value.trim()) return partners.value;

  const q = search.value.toLowerCase();
  return partners.value.filter((p) => {
    const name = String(p.name || "").toLowerCase();
    const website = String(p.website_url || "").toLowerCase();
    return name.includes(q) || website.includes(q);
  });
});

/* =========================
   ACTIONS
========================= */
async function deletePartner(id) {
  if (!confirm("Are you sure you want to delete this partner?")) return;

  try {
    await apiClient.delete(`/partners/${id}`);
    alert("Partner deleted successfully!");
    fetchPartners();
  } catch (error) {
    alert("Failed to delete partner.");
  }
}

/* =========================
   LIFECYCLE
========================= */
onMounted(fetchPartners);
</script>

<style scoped>
table {
  border-radius: 0.5rem;
  overflow: hidden;
}
</style>
